/**
 * contenteditable のノード本文に対するキャレット（カーソル）操作。
 *
 * どれも渡された要素と現在の選択範囲だけを見る純粋なDOM操作で、
 * アウトラインの状態には触れない。通常の編集画面とカードビューの
 * 両方から使う。
 *
 * 位置は「要素内のテキスト先頭からの文字数」で表す。DOM のテキスト
 * ノードは装飾やマークダウンの描画で分割されるため、テキストノードと
 * オフセットの組をそのまま持ち回らない。
 */

import { containingElement } from './dom.js';

export function placeCaretAtEnd(el) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

export function placeCaretAtTextEdge(el, edge) {
  const offset = edge === 'start' ? 0 : (el.textContent || '').length;
  setTextSelection(el, offset, offset);
}

export function textSelectionOffsets(el) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const text = containingElement(range.commonAncestorContainer)?.closest('.node-text');
  if (text !== el) {
    return null;
  }

  return {
    start: textOffsetWithin(el, range.startContainer, range.startOffset),
    end: textOffsetWithin(el, range.endContainer, range.endOffset),
  };
}

export function isCaretAtTextEnd(el) {
  const offsets = textSelectionOffsets(el);
  const textLength = (el.textContent || '').length;
  return offsets !== null && offsets.start === textLength && offsets.end === textLength;
}

export function isCaretOnTextEdgeLine(el, edge) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
    return false;
  }

  const range = selection.getRangeAt(0);
  if (containingElement(range.commonAncestorContainer)?.closest('.node-text') !== el) {
    return false;
  }

  const caretRect = range.getClientRects()[0] || range.getBoundingClientRect();
  const contentRange = document.createRange();
  contentRange.selectNodeContents(el);
  const lineRects = Array.from(contentRange.getClientRects()).filter((rect) => rect.height > 0);

  // 空のノードなど、ブラウザが文字範囲を返さない場合は1行だけとみなす。
  if (lineRects.length === 0 || caretRect.height === 0) {
    return true;
  }

  const caretCenterY = caretRect.top + caretRect.height / 2;
  const firstLineCenterY = Math.min(...lineRects.map((rect) => rect.top + rect.height / 2));
  const lastLineCenterY = Math.max(...lineRects.map((rect) => rect.top + rect.height / 2));
  const tolerance = Math.max(2, caretRect.height / 3);
  return edge === 'start' ? caretCenterY <= firstLineCenterY + tolerance : caretCenterY >= lastLineCenterY - tolerance;
}

export function textOffsetWithin(el, node, offset) {
  const range = document.createRange();
  range.selectNodeContents(el);
  try {
    range.setEnd(node, offset);
  } catch (error) {
    return el.textContent.length;
  }
  return range.toString().length;
}

export function nodeAndOffsetAtTextOffset(el, offset) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let remaining = Math.max(0, offset);
  let textNode = walker.nextNode();

  while (textNode) {
    const length = textNode.nodeValue.length;
    if (remaining <= length) {
      return { node: textNode, offset: remaining };
    }
    remaining -= length;
    textNode = walker.nextNode();
  }

  if (!el.firstChild) {
    el.append(document.createTextNode(''));
  }
  const lastNode = el.lastChild;
  return {
    node: lastNode,
    offset: lastNode.nodeType === Node.TEXT_NODE ? lastNode.nodeValue.length : lastNode.childNodes.length,
  };
}

export function setTextSelection(el, start, end) {
  const selection = window.getSelection();
  const range = document.createRange();
  const rangeStart = nodeAndOffsetAtTextOffset(el, start);
  const rangeEnd = nodeAndOffsetAtTextOffset(el, end);
  range.setStart(rangeStart.node, rangeStart.offset);
  range.setEnd(rangeEnd.node, rangeEnd.offset);
  selection.removeAllRanges();
  selection.addRange(range);
}
